// fillContract.js
// Fills Sal's contract templates with whatever the student typed in the app.
// Works in the browser or in Node. Needs one package: npm install pdf-lib
//
// Templates (put them in your app's public folder or storage):
//   Purchase_Contract_Fillable.pdf       -> house contract (cash)
//   Land_Purchase_Contract_Fillable.pdf  -> land contract
//   Mortgage_Takeover_Contract.pdf       -> subject-to / mortgage takeover
//   Seller_Finance_Contract.pdf          -> seller finance
//
// ---------------------------------------------------------------------------
// FIELD NAMES (use these as the keys in the data you pass in)
//
// All four contracts:
//   property_address_1, property_address_2, apn,
//   earnest_money, purchase_price, coe_date,
//   escrow_name, escrow_address, escrow_officer, escrow_email,
//   seller_names, buyer_name, inspection_days,
//   seller1_printed_name, seller1_mailing_address, seller1_email, seller1_phone,
//   seller2_printed_name, seller2_mailing_address, seller2_email, seller2_phone,
//   buyer_company_name, buyer_signer_name, buyer_email, buyer_phone
//   Checkboxes: escrow_fees_split, escrow_fees_buyer, title_policy_seller, title_policy_buyer,
//               hoa_fees_split, hoa_fees_buyer
//
// House + land: section_1_11 (personal property / rights included), section_1_12 (additional terms)
//
// Land only: county_state, acreage
//   (inspection_days fills the Due Diligence Period)
//   Checkboxes: survey_seller, survey_buyer, survey_not_required
//
// Mortgage takeover only:
//   loan_servicer, loan_number, loan_balance, loan_payment, loan_rate, loan_arrears, other_liens,
//   subto_amount, cash_to_seller, arrears_paid,
//   equity_carry_amount, equity_carry_payment, equity_carry_months, equity_carry_rate,
//   servicing_company,
//   section_1_14 (personal property), section_1_15 (additional terms)
//   Checkboxes: loan_rate_fixed, loan_rate_adjustable, loan_status_current, loan_status_behind,
//               impound_to_buyer, impound_reimbursed,
//               servicing_via_company, servicing_direct   (how loan payments get made)
//
// Seller finance only:
//   down_payment, amount_financed, interest_rate, monthly_payment, amortization_years,
//   first_payment_date, balloon_months, late_fee, late_days, servicing_company,
//   section_1_13 (personal property), section_1_14 (additional terms)
//   Checkboxes: payment_pi, payment_io, lender_policy_seller, lender_policy_buyer,
//               note_nonrecourse, note_recourse, note_assumable, note_due_on_sale,
//               servicing_via_company, servicing_direct   (how payments get made)
//
// servicing_company is only needed when servicing_via_company is checked.
//
// Notes:
//   - Don't type "$" or "%" in number fields. The contract already prints them.
//   - coe_date: on the house and land contracts it's just the month and day
//     (the contract already says ", 2026"). On the mortgage takeover and seller
//     finance contracts, type the full date.
//   - Signatures, initials, and signing dates are left blank on purpose
//     so your e-sign tool handles them.
// ---------------------------------------------------------------------------

import { PDFDocument, PDFName, PDFArray, PDFRef } from 'pdf-lib';

/**
 * @param {ArrayBuffer|Uint8Array} templateBytes  the fillable PDF template
 * @param {Object} data  { field_name: value }  strings for text, true/false for checkboxes
 * @param {Object} [opts]
 * @param {boolean} [opts.lock=true]  flatten so the finished contract can't be edited
 * @returns {Promise<Uint8Array>} the filled PDF
 */
export async function fillContract(templateBytes, data, { lock = true } = {}) {
  const pdf = await PDFDocument.load(templateBytes);
  const form = pdf.getForm();
  const names = new Set(form.getFields().map((f) => f.getName()));

  for (const [name, value] of Object.entries(data)) {
    if (!names.has(name) || value === undefined || value === null) continue;
    if (typeof value === 'boolean') {
      const box = form.getCheckBox(name);
      value ? box.check() : box.uncheck();
    } else {
      form.getTextField(name).setText(String(value));
    }
  }

  if (lock) {
    form.flatten();
    // pdf-lib can leave dead links to the removed fields on each page. Clean them up.
    for (const page of pdf.getPages()) {
      const annots = page.node.lookup(PDFName.of('Annots'));
      if (!(annots instanceof PDFArray)) continue;
      const keep = annots.asArray().filter((a) => !(a instanceof PDFRef) || pdf.context.lookup(a));
      page.node.set(PDFName.of('Annots'), pdf.context.obj(keep));
    }
  }
  return pdf.save();
}

/* ---------------- Example (browser) ----------------

const type = deal.isLand ? 'Land_Purchase_Contract_Fillable.pdf' : 'Purchase_Contract_Fillable.pdf';
const template = await fetch(`/contracts/${type}`).then((r) => r.arrayBuffer());

const bytes = await fillContract(template, {
  property_address_1: '1234 Main St',
  property_address_2: 'Albuquerque, NM 87102',
  apn: '101-205-334',
  earnest_money: '1,000',
  purchase_price: '181,423',
  coe_date: 'November 20',
  seller_names: 'John Doe and Mary Doe',
  buyer_name: 'Gallardo Luxe LLC and/or assigns',
  inspection_days: '10',
  escrow_fees_split: true,
  title_policy_seller: true,
});

// download it
const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
const a = Object.assign(document.createElement('a'), { href: url, download: 'Purchase_Contract.pdf' });
a.click();

----------------------------------------------------- */
